import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { kmsService } from '@aaas/shared';
import type { DriveIntegrationCreate, DriveIntegrationUpdate } from '@aaas/shared';

// ========================================
// QUERIES
// ========================================

interface UseListDriveIntegrationsOptions {
  idToken: string | undefined;
  spaceId: string | undefined;
  enabled?: boolean;
  refetchInterval?: number | false;
}

export const useListDriveIntegrations = ({
  idToken,
  spaceId,
  enabled = true,
  refetchInterval = false,
}: UseListDriveIntegrationsOptions) => {
  return useQuery({
    queryKey: ['kms-drive-integrations', spaceId],
    queryFn: async () => {
      if (!idToken) throw new Error('Authentication token required');
      if (!spaceId) throw new Error('Process ID required');
      return kmsService.listDriveIntegrations(idToken, spaceId);
    },
    enabled: enabled && !!idToken && !!spaceId,
    refetchInterval,
    refetchIntervalInBackground: false,
    staleTime: 10000,
    refetchOnWindowFocus: true,
  });
};

interface UseGetDriveIntegrationOptions {
  idToken: string | undefined;
  spaceId: string | undefined;
  integrationId: string | undefined;
  enabled?: boolean;
}

export const useGetDriveIntegration = ({
  idToken,
  spaceId,
  integrationId,
  enabled = true,
}: UseGetDriveIntegrationOptions) => {
  return useQuery({
    queryKey: ['kms-drive-integration', spaceId, integrationId],
    queryFn: async () => {
      if (!idToken) throw new Error('Authentication token required');
      if (!spaceId) throw new Error('Process ID required');
      if (!integrationId) throw new Error('Integration ID required');
      return kmsService.getDriveIntegration(idToken, spaceId, integrationId);
    },
    enabled: enabled && !!idToken && !!spaceId && !!integrationId,
    staleTime: 10000,
  });
};

interface UseListDriveSyncedFilesOptions {
  idToken: string | undefined;
  spaceId: string | undefined;
  integrationId: string | undefined;
  enabled?: boolean;
}

export const useListDriveSyncedFiles = ({
  idToken,
  spaceId,
  integrationId,
  enabled = true,
}: UseListDriveSyncedFilesOptions) => {
  return useQuery({
    queryKey: ['kms-drive-synced-files', spaceId, integrationId],
    queryFn: async () => {
      if (!idToken) throw new Error('Authentication token required');
      if (!spaceId) throw new Error('Process ID required');
      if (!integrationId) throw new Error('Integration ID required');
      return kmsService.listDriveSyncedFiles(idToken, spaceId, integrationId);
    },
    enabled: enabled && !!idToken && !!spaceId && !!integrationId,
    staleTime: 5000,
  });
};

// ========================================
// MUTATIONS
// ========================================

export const useCreateDriveIntegration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      idToken,
      spaceId,
      data,
    }: {
      idToken: string;
      spaceId: string;
      data: DriveIntegrationCreate;
    }) => {
      return await kmsService.createDriveIntegration(idToken, spaceId, data);
    },
    onSuccess: (_, variables) => {
      // Invalidate and refetch integrations list for this process
      queryClient.invalidateQueries({ queryKey: ['kms-drive-integrations', variables.spaceId] });
    },
  });
};

export const useUpdateDriveIntegration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      idToken,
      spaceId,
      integrationId,
      updates,
    }: {
      idToken: string;
      spaceId: string;
      integrationId: string;
      updates: DriveIntegrationUpdate;
    }) => {
      return await kmsService.updateDriveIntegration(idToken, spaceId, integrationId, updates);
    },
    onSuccess: (_, variables) => {
      // Invalidate specific integration and list
      queryClient.invalidateQueries({
        queryKey: ['kms-drive-integration', variables.spaceId, variables.integrationId],
      });
      queryClient.invalidateQueries({ queryKey: ['kms-drive-integrations', variables.spaceId] });
    },
  });
};

export const useDeleteDriveIntegration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      idToken,
      spaceId,
      integrationId,
      deleteDocuments,
    }: {
      idToken: string;
      spaceId: string;
      integrationId: string;
      deleteDocuments: boolean;
    }) => {
      return await kmsService.deleteDriveIntegration(
        idToken,
        spaceId,
        integrationId,
        deleteDocuments
      );
    },
    onSuccess: (_, variables) => {
      // Invalidate integrations list for this process
      queryClient.invalidateQueries({ queryKey: ['kms-drive-integrations', variables.spaceId] });
    },
  });
};

export const useTriggerDriveSync = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      idToken,
      spaceId,
      integrationId,
      deleteExisting = false,
    }: {
      idToken: string;
      spaceId: string;
      integrationId: string;
      deleteExisting?: boolean;
    }) => {
      return await kmsService.triggerDriveSync(idToken, spaceId, integrationId, deleteExisting);
    },
    onSuccess: (_, variables) => {
      // Invalidate integration data to get updated sync stats
      queryClient.invalidateQueries({
        queryKey: ['kms-drive-integration', variables.spaceId, variables.integrationId],
      });
      queryClient.invalidateQueries({ queryKey: ['kms-drive-integrations', variables.spaceId] });
      queryClient.invalidateQueries({
        queryKey: ['kms-drive-synced-files', variables.spaceId, variables.integrationId],
      });
      // Invalidate documents list if deleting and re-creating
      if (variables.deleteExisting) {
        queryClient.invalidateQueries({
          queryKey: ['kms-documents', variables.spaceId],
        });
      }
    },
  });
};

export const useSyncDriveFileToProcess = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      idToken,
      spaceId,
      integrationId,
      driveFileId,
    }: {
      idToken: string;
      spaceId: string;
      integrationId: string;
      driveFileId: string;
    }) => {
      return await kmsService.syncDriveFileToProcess(idToken, spaceId, integrationId, driveFileId);
    },
    onSuccess: (_, variables) => {
      // Invalidate documents list for the process
      queryClient.invalidateQueries({
        queryKey: ['kms-documents', variables.spaceId],
      });
      // Invalidate synced files to update sync status
      queryClient.invalidateQueries({
        queryKey: ['kms-drive-synced-files', variables.spaceId, variables.integrationId],
      });
      // Invalidate integration stats
      queryClient.invalidateQueries({
        queryKey: ['kms-drive-integration', variables.spaceId, variables.integrationId],
      });
    },
  });
};
